/**
 * @fileoverview Reactivate User dialog hook.
 * @description The Reactivate dialog's data and command: reads the user's details afresh (whose
 * ETag the command carries as If-Match), the lifecycle configuration and the Sales roles the admin
 * may bring a user back as (the shared assignable-roles query, also used by Add and Invite User),
 * and sends the reactivation. The success confirmation and the close run in the mutation's own
 * callback, so they still happen if the dialog unmounts while the request is in flight, and
 * closing is held while the request is pending. A stale-version refusal (412 / 3022) re-reads the
 * details.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useMemo } from 'react'

import { useFreshUserDetail } from '@/features/users/hooks/useFreshUserDetail'
import { useReactivateUser } from '@/features/users/hooks/useReactivateUser'
import { useUserManagementConfiguration } from '@/features/users/hooks/useUserManagementConfiguration'
import { selectSalesRoles } from '@/features/users/utils/reactivation.utils'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'
import { useAssignableRoles } from '@/shared/hooks/useAssignableRoles'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import type { ReactivatedUser, ReactivateUserRequest } from '@/features/users/types/users.types'

interface ReactivateUserDialogOptions {
  userId: string
  onClose: () => void
  onReactivated: (result: ReactivatedUser) => void
}

/**
 * @description Loads what the Reactivate dialog needs and runs the reactivation.
 * @param {ReactivateUserDialogOptions} options - The user, and the dialog's callbacks.
 * @param {string} options.userId - The user to reactivate.
 * @param {() => void} options.onClose - Dismisses the dialog.
 * @param {(result: ReactivatedUser) => void} options.onReactivated - Called with the reactivated
 * user and how they came back, after a successful reactivation and before the dialog closes.
 * @returns {object} The loaded data, the load states, whether the command may be sent, the close
 * handler (held while the command is pending) and the command.
 */
export function useReactivateUserDialog({
  userId,
  onClose,
  onReactivated,
}: ReactivateUserDialogOptions) {
  const detailQuery = useFreshUserDetail(userId)
  const configurationQuery = useUserManagementConfiguration()
  const rolesQuery = useAssignableRoles(true)
  const reactivateUser = useReactivateUser({
    /**
     * @description Reports the reactivated user, then closes the dialog. Runs even when the
     * dialog has unmounted since the request was sent.
     * @param {ReactivatedUser} result - The user as they are now, and how they came back.
     */
    onSuccess: (result) => {
      onReactivated(result)
      onClose()
    },
  })
  const close = useGuardedClose(onClose, reactivateUser.isPending)
  const detail = detailQuery.detail
  const configuration = configurationQuery.data
  const roles = useMemo(
    /**
     * @description The Sales roles among the assignable ones, once they have loaded.
     * @returns {ReactivationRoleOption[] | undefined} The Sales roles, or undefined while loading.
     */
    () => (rolesQuery.data ? selectSalesRoles(rolesQuery.data) : undefined),
    [rolesQuery.data],
  )
  // The list's advisory actions can be stale: once the user is re-read, only offer what's allowed.
  const allowed = detail?.user.allowedActions.includes('REACTIVATE') ?? false
  const loadError = detailQuery.error ?? configurationQuery.error ?? rolesQuery.error

  /**
   * @description Reactivates the user against the version the form was filled from. The
   * Idempotency-Key is kept for this action on this user while its outcome is unknown, so sending
   * it again after a lost response is a replay (no second welcome-back email or invitation restart),
   * not a new command.
   * @param {ReactivateUserRequest} payload - The role, manager, reason and note.
   */
  const submit = (payload: ReactivateUserRequest) => {
    if (!detail) {
      return
    }
    reactivateUser.mutate(
      { userId, payload, etag: detail.etag },
      {
        /**
         * @description Re-reads the user when someone else changed them since the dialog loaded.
         * @param {ApiError} error - The refusal.
         */
        onError: (error) => {
          if (error.code === ERROR_CODES.VERSION_CONFLICT) {
            void detailQuery.refetch()
          }
        },
      },
    )
  }

  return {
    detail,
    configuration,
    roles,
    allowed,
    loadError,
    loading:
      (detailQuery.isPending || configurationQuery.isPending || rolesQuery.isPending) && !loadError,
    canSubmit: Boolean(detail && configuration && roles && allowed),
    close,
    submit,
    isSubmitting: reactivateUser.isPending,
    submitError: reactivateUser.error,
  }
}
